import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Truck, CreditCard, ShieldCheck, Clock, Wallet, Package, MessageCircle, ShoppingBag, BadgePercent, AlertTriangle, Cookie } from "lucide-react";
import { FaStripe, FaCcVisa, FaCcMastercard, FaPaypal } from "react-icons/fa";
import { useChat } from "../../context/ChatContext";
import { useCart } from "../../context/CartContext";
import { useTheme } from "../../context/ThemeContext";
import { codProvinces, codMinAmount, formatProvinceList, freeShippingProvinces, zoneCapsForProvince } from "../../utils/shipping";
import { comuniListText } from "../../utils/comuni";
import { sectionBgColor } from "../../utils/themeBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/wingman/7b8b7474-e52e-4388-adc0-a2361520c0a4/attachments/8840390a845949e6b9092405676c1743_image.png";

const NAVY_BG =
  "https://static.prod-images.emergentagent.com/jobs/0ea60aca-0e0a-481c-bb77-149fc760bfd5/images/35d18311c34d263ff85f58a8f1c1982358715c5660a107308bc4ad3f2b08884e.png";

const COMPANY_FALLBACK = {
  company_name: "LEOVERA Nutrition di Davide Allibrio",
  partita_iva: "02155960897",
  rea: "SR-472359",
  pec: "d.allibriopec@pec.it",
  sede_legale: "",
  uibm_protocol: "302026000158266",
  trademark_notice: "Marchio registrato UIBM — Protocollo n. 302026000158266 ™",
};

// Mirrors backend/settings.py DEFAULTS["commerce"]["footer_shipping_text"] so
// the footer still reads correctly if the admin clears the field to blank.
const DEFAULT_FOOTER_SHIPPING_TEXT =
  "Spedizione gratuita nei comuni di {comuni}. Nel resto d'Italia spedizione gratuita " +
  "sopra i {soglia}. Pagamento alla consegna disponibile nelle province di {province}.";

const Footer = () => {
  const { setOpen: openChat } = useChat();
  const { setOpen: openCart } = useCart();
  const { settings } = useTheme();
  const [company, setCompany] = useState(COMPANY_FALLBACK);

  const codList = formatProvinceList(codProvinces(settings));
  const freeShipThreshold = Number(settings?.commerce?.free_shipping_threshold || 0);
  const codMin = codMinAmount(settings);
  const freeShipComuniCaps = [...new Set(freeShippingProvinces(settings).flatMap((p) => zoneCapsForProvince(p, settings)))];
  const freeShipComuniText = comuniListText(freeShipComuniCaps, settings) || "Siracusa e Catania";
  const codMinNote = codMin > 0 ? ` (ordine minimo ${codMin.toFixed(2)}€)` : "";
  const footerShippingText = (settings?.commerce?.footer_shipping_text || "").trim() || DEFAULT_FOOTER_SHIPPING_TEXT;
  const freeShipThresholdText = freeShipThreshold > 0 ? `${freeShipThreshold.toFixed(0)}€` : "100€";
  const footerShippingLine = footerShippingText
    .replaceAll("{comuni}", freeShipComuniText)
    .replaceAll("{soglia}", freeShipThresholdText)
    .replaceAll("{province}", `${codList || "—"}${codMinNote}`);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(({ data }) => {
        if (data.company?.partita_iva) setCompany(data.company);
      })
      .catch(() => {});
  }, []);

  return (
    <footer
      id="contatti"
      data-testid="footer-section"
      className="relative overflow-hidden border-t border-white/10"
      style={{ backgroundColor: sectionBgColor(settings, "footer") }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: `url(${NAVY_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#060A14]/80 via-[#060A14]/95 to-[#060A14]" />

      {/* CTA band */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28 border-b border-white/10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end"
        >
          <div className="lg:col-span-8">
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#C0C0C0]">
              Pronto a partire?
            </span>
            <h2
              className="mt-4 font-[Outfit] text-white tracking-tight font-light leading-[1.05]"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
            >
              Parla con Leo,{" "}
              <span className="italic font-extralight text-[#C0C0C0]">
                il tuo consulente di nutrizione.
              </span>
            </h2>
            <p className="mt-6 max-w-2xl font-[Manrope] text-[#94A3B8] text-base sm:text-lg leading-relaxed">
              Leo (AI) ti aiuta a scegliere i prodotti giusti, risponde alle tue domande
              su ingredienti e dosaggi e finalizza l'ordine in chat. Puoi anche pagare
              subito con carta tramite il carrello.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-emerald-300 text-[12px] tracking-[0.04em] normal-case" data-testid="footer-shipping-text">
              <BadgePercent className="w-3.5 h-3.5 shrink-0" />
              {footerShippingLine}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
            <button
              onClick={() => openChat(true)}
              data-testid="footer-chat-cta"
              className="inline-flex items-center gap-3 bg-emerald-500 text-white px-8 py-4 rounded-sm text-[12px] tracking-[0.28em] uppercase font-medium hover:bg-emerald-400 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Parla con Leo
            </button>
            <button
              onClick={() => openCart(true)}
              data-testid="footer-cart-cta"
              className="inline-flex items-center gap-3 border border-[#C0C0C0]/40 text-[#E5E4E2] px-8 py-4 rounded-sm text-[12px] tracking-[0.28em] uppercase hover:bg-[#C0C0C0]/10 transition-all duration-300"
            >
              <ShoppingBag className="w-5 h-5 text-[#C0C0C0]" />
              Apri il carrello
            </button>
          </div>
        </motion.div>
      </div>

      {/* Info row */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 border-b border-white/10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Truck, title: "Spedizione", desc: "3-5 giorni lavorativi in tutta Italia." },
            { icon: CreditCard, title: "Pagamenti", desc: "Carta · PayPal · Contanti alla consegna." },
            { icon: Package, title: "Cash on delivery", desc: `Solo ${codList || "—"}${codMinNote ? ` ${codMinNote.trim()}` : ""}.` },
            { icon: ShieldCheck, title: "Brand certificati", desc: "Anderson · Eurosup · Prolabs." },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} data-testid={`footer-info-${b.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon className="w-5 h-5 text-[#C0C0C0] mb-3" strokeWidth={1.5} />
                <h4 className="font-[Outfit] text-white text-base tracking-tight mb-1">
                  {b.title}
                </h4>
                <p className="text-[13px] text-[#94A3B8] leading-relaxed">
                  {b.desc}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 pt-8 border-t border-white/5 flex flex-wrap items-center justify-between gap-6">
          <span className="text-[10px] tracking-[0.32em] uppercase text-[#94A3B8] flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
            Leo (AI) attivo 24/7
          </span>
          <div className="flex items-center gap-5 text-[#C0C0C0]" data-testid="footer-payment-icons">
            <FaCcVisa className="w-8 h-8" title="Visa" />
            <FaCcMastercard className="w-8 h-8" title="Mastercard" />
            <FaStripe className="w-10 h-10" title="Stripe" />
            <FaPaypal className="w-6 h-6" title="PayPal" />
            <Wallet className="w-5 h-5" strokeWidth={1.5} title="Contanti" />
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col gap-6">
        {/* Legal disclaimer for food supplements (Italian law) */}
        <div
          data-testid="footer-supplement-disclaimer"
          className="border border-amber-500/20 bg-amber-500/5 rounded-sm px-4 py-3 flex items-start gap-3"
        >
          <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" strokeWidth={1.5} />
          <p className="text-[11px] leading-relaxed text-[#E5E4E2]">
            <span className="tracking-[0.24em] uppercase text-amber-200 text-[10px] block mb-1">Avvertenze legali · Integratori alimentari</span>
            Gli integratori alimentari non vanno intesi come sostituti di una dieta variata ed
            equilibrata e di uno stile di vita sano. Non superare le dosi giornaliere consigliate.
            Tenere fuori dalla portata dei bambini al di sotto dei 3 anni. Consultare il proprio
            medico prima dell'assunzione, in caso di gravidanza, allattamento, patologie in corso
            o assunzione di farmaci. I prodotti non intendono diagnosticare, curare o prevenire
            alcuna malattia.
          </p>
        </div>

        {/* Legal links row */}
        <nav
          data-testid="footer-legal-links"
          className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]"
        >
          <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <span className="text-white/15">·</span>
          <Link to="/cookie-policy" data-testid="footer-link-cookie-policy" className="hover:text-white transition-colors">
            Cookie Policy
          </Link>
          <span className="text-white/15">·</span>
          <Link to="/termini" data-testid="footer-link-terms" className="hover:text-white transition-colors">
            Termini e Condizioni
          </Link>
          <span className="text-white/15">·</span>
          <Link to="/modulo-recesso" data-testid="footer-link-recesso" className="hover:text-white transition-colors">
            Modulo di Recesso
          </Link>
          <span className="text-white/15">·</span>
          <Link to="/recesso" data-testid="footer-link-recesso-online" className="hover:text-white transition-colors">
            Recedi dal contratto qui
          </Link>
          <span className="text-white/15">·</span>
          <button
            type="button"
            data-testid="footer-link-cookie-preferences"
            onClick={() => window.dispatchEvent(new CustomEvent("leovera:open-cookie-preferences"))}
            className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
          >
            <Cookie className="w-3 h-3" />
            Preferenze cookie
          </button>
        </nav>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-4 border-t border-white/5">
        <div className="flex items-center gap-3" data-testid="footer-logo">
          <img
            src={LOGO_URL}
            alt="LEOVERA Nutrition"
            className="h-12 w-auto object-contain rounded-md bg-white p-1.5 shadow-[0_0_0_1px_rgba(192,192,192,0.25)]"
          />
          <span className="text-[10px] text-[#94A3B8] tracking-[0.32em] uppercase">V104.3</span>
        </div>

        <p className="text-[11px] text-[#94A3B8] tracking-[0.18em] uppercase">
          © {new Date().getFullYear()} LEOVERA Nutrition · Tutti i diritti riservati
        </p>

        <button
          onClick={() => openChat(true)}
          data-testid="footer-chat-link"
          className="text-[11px] tracking-[0.28em] uppercase text-[#E5E4E2] hover:text-[#C0C0C0] transition-colors inline-flex items-center gap-2"
        >
          <MessageCircle className="w-4 h-4 text-emerald-400" />
          Assistenza Leo
        </button>
        </div>

        {/* Mandatory company identification (D.lgs. 70/2003 art. 7) */}
        {company && (
          <p data-testid="footer-company-data"
             className="text-[10px] text-[#94A3B8]/80 tracking-[0.08em] leading-relaxed pt-4 border-t border-white/5">
            {company.company_name} — P.IVA: {company.partita_iva} — REA: {company.rea} — PEC: {company.pec}
            {company.sede_legale ? ` — Sede legale: ${company.sede_legale}` : ""}
          </p>
        )}

        {/* UIBM registered trademark notice */}
        {company?.trademark_notice && (
          <p data-testid="footer-trademark-notice"
             className="text-[10px] text-[#C0C0C0]/90 tracking-[0.14em] uppercase leading-relaxed">
            {company.trademark_notice}
          </p>
        )}
      </div>
    </footer>
  );
};

export default Footer;
